import { html, nothing, type TemplateResult } from 'lit';
import type { ServiceNowTicket } from '../../db/ServiceNowTicket.js';
import * as m from '../../localization/out/messages.js';
import { googleMapsUrl, type MailRecipients, mailtoHref, windowDate, windowLabel } from './_colleague-format.js';
import { notesBlock } from './_notes-block.js';
import { STATE_BADGE_CLASS } from './_state-badge.js';

/**
 * The ticket card shared by the FE's own Tickets list and the Colleagues list, so a ticket reads the
 * same in both and a layout change is made once.
 *
 * Layout: number + engineer on the left, schedule on the right, then the title, then the status badge
 * above the info lines (address, coordinates, customer, phone, part numbers), then the notes. The parts STATE is the
 * puzzle icon on the right-hand rail.
 *
 * What differs between the two lists is passed in rather than branched on:
 * - `href` makes the card clickable (own list). The title becomes a link whose ::after covers the card,
 *   so the coordinate and phone links can sit on top of it — a nested <a> inside an <a> is invalid.
 * - `showDate` adds the date to the schedule label (the own list spans many days; Colleagues is one day).
 * - the `*Extra` / `footer` slots carry the bits only one list has (On Route badge, ETA chips, the
 *   colleague parts/reply-code rail, the "Email service desk" button).
 */
export interface TicketCardOptions {
  /** Detail page URL. When set the whole card is clickable. */
  href?: string;
  /** Prefix the schedule label with the window's date. */
  showDate?: boolean;
  /** Extra lines under the schedule label on the right-hand rail. */
  railExtra?: TemplateResult | typeof nothing;
  /** Rendered next to the state badge (e.g. the On Route substate). */
  badgeExtra?: TemplateResult | typeof nothing;
  /** Rendered as the first info line, above the address (e.g. the ETA window). */
  infoExtra?: TemplateResult | typeof nothing;
  /** Rendered under the notes (e.g. the "Email service desk" button). */
  footer?: TemplateResult | typeof nothing;
}

function scheduleLabel(ticket: ServiceNowTicket, showDate: boolean) {
  // An explicit expected start wins when SN provides one; otherwise the scheduling window carries it.
  if (showDate && ticket.expectedStartDisplay) return ticket.expectedStartDisplay;
  const slot = windowLabel(ticket.windowStart, ticket.windowEnd);
  return showDate && ticket.windowStart ? html`${windowDate(ticket.windowStart)} · ${slot}` : slot;
}

/**
 * The puzzle icon: the state of the parts, in ServiceNow's own words ("Parts available", "Parts at PUDO",
 * "Labor only"). A dedicated parts status wins if ServiceNow ever sends one. Nothing is drawn when there is no
 * state, since an empty icon would only look unfinished. (The legacy tool's "NotFound" has no source here.)
 */
function partsState(ticket: ServiceNowTicket) {
  const label = ticket.partsStatusLabel || ticket.fulfilmentStatusLabel;
  if (!label) return nothing;
  return html`<span class="flex items-center gap-1 text-base-content/70" title=${label}>
    <span class="icon-[material-symbols--extension-outline] text-sm"></span>
    ${label}
  </span>`;
}

export function TicketCard(ticket: ServiceNowTicket, opts: TicketCardOptions = {}) {
  const { href, showDate = false, railExtra = nothing, badgeExtra = nothing, infoExtra = nothing, footer = nothing } = opts;
  const hasCoords = typeof ticket.locationLat === 'number' && typeof ticket.locationLng === 'number';
  const partLabels = (ticket.parts ?? [])
    .filter(p => p.modelNumber)
    .map(p => (p.quantity && Number(p.quantity) > 1 ? `${p.modelNumber} ×${p.quantity}` : p.modelNumber!));

  const title = href
    ? html`<a
        href=${href}
        class="text-sm line-clamp-2 no-underline text-base-content after:absolute after:inset-0 after:content-['']"
        >${ticket.title || m.ticket_no_title()}</a
      >`
    : ticket.title
      ? html`<span class="text-sm">${ticket.title}</span>`
      : nothing;

  return html`
    <div
      class="rounded-xl border border-base-300 bg-base-100 p-4 flex flex-col gap-2 ${href
        ? 'relative hover:bg-base-200 transition-colors cursor-pointer text-base-content'
        : ''}"
    >
      <div class="flex items-start justify-between gap-2">
        <div class="flex flex-col gap-1 min-w-0">
          <span class="font-mono text-xs bg-base-200 px-2 py-0.5 rounded self-start">${ticket.number}</span>
          ${ticket.assignedToName
            ? html`<span class="text-sm font-semibold truncate">${ticket.assignedToName}</span>`
            : nothing}
        </div>

        <div class="flex flex-col items-end gap-1 shrink-0 text-xs">
          <span class="flex items-center gap-1 text-base-content/70" title=${m.colleague_filter_date()}>
            <span class="icon-[material-symbols--schedule-outline] text-sm"></span>
            ${scheduleLabel(ticket, showDate)}
          </span>
          ${partsState(ticket)}
          ${railExtra}
        </div>
      </div>

      ${title}

      <div class="flex flex-col gap-1 text-xs text-base-content/60">
        ${ticket.stateLabel || badgeExtra !== nothing
          ? html`<div class="flex items-center gap-1.5 flex-wrap mb-1">
              ${ticket.stateLabel
                ? html`<span class="badge badge-sm ${STATE_BADGE_CLASS[ticket.state ?? ''] ?? 'badge-neutral'}"
                    >${ticket.stateLabel}</span
                  >`
                : nothing}
              ${badgeExtra}
            </div>`
          : nothing}
        ${infoExtra}
        ${ticket.locationLabel
          ? html`<div class="flex items-center gap-1">
              <span class="icon-[material-symbols--location-on-outline] text-sm shrink-0"></span>
              <span class="text-pretty">${ticket.locationLabel}</span>
            </div>`
          : nothing}
        ${hasCoords
          ? html`<div class="flex items-center gap-1">
              <span class="icon-[material-symbols--my-location] text-sm"></span>
              <a
                class="link link-primary ${href ? 'relative z-10' : ''}"
                href=${googleMapsUrl(ticket.locationLat!, ticket.locationLng!)}
                target="_blank"
                rel="noopener noreferrer"
              >
                ${ticket.locationLat!.toFixed(5)}, ${ticket.locationLng!.toFixed(5)}
              </a>
            </div>`
          : nothing}
        ${ticket.clientName
          ? html`<div class="flex items-center gap-1">
              <span class="icon-[material-symbols--apartment] text-sm"></span>${ticket.clientName}
            </div>`
          : nothing}
        ${ticket.contactPhone
          ? html`<div class="flex items-center gap-1">
              <span class="icon-[material-symbols--call-outline] text-sm"></span>
              <a class="link ${href ? 'relative z-10' : ''}" href="tel:${ticket.contactPhone}">${ticket.contactPhone}</a>
            </div>`
          : nothing}
        ${partLabels.length > 0
          ? html`<div class="flex items-center gap-1">
              <span class="icon-[material-symbols--inventory-2-outline] text-sm shrink-0"></span>
              <span class="text-pretty">${partLabels.join(', ')}</span>
            </div>`
          : nothing}
      </div>

      ${notesBlock(ticket.workNotes)} ${footer}
    </div>
  `;
}

/**
 * "Email service desk" button, shared by both lists. Pass `overlay` when it sits inside a clickable
 * card, so it stacks above the card's stretched title link instead of being swallowed by it.
 */
export function ServiceDeskButton(
  ticket: ServiceNowTicket,
  recipients: MailRecipients | null | undefined,
  opts: { overlay?: boolean } = {},
) {
  // No answer at all means this user does not get the button (e.g. a customer) or it has not loaded yet.
  // An answer with empty recipients still shows it: the mail opens with the address left blank.
  if (!recipients) return nothing;
  const href = mailtoHref(ticket, recipients);
  return html`
    <div class="flex justify-end">
      <a
        class="btn btn-xs btn-outline btn-primary gap-1 inline-flex items-center leading-none ${opts.overlay ? 'relative z-10' : ''}"
        href=${href}
      >
        <span class="icon-[material-symbols--mail-outline] text-sm shrink-0"></span>
        <span>${m.colleague_email_button()}</span>
      </a>
    </div>
  `;
}
